import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DINACHARYA_LINK_KEYS } from "@/lib/dinacharyaLinks";

const FIELDS = [
  ["name", "Display name"],
  ["legal_name", "Legal name"],
  ["industry", "Industry"],
  ["country", "Country"],
  ["engagement_owner", "Engagement owner"],
];
const BLANK = { name: "", legal_name: "", industry: "", country: "", engagement_owner: "" };
export default function AdminTenants() {
  const { reload, switchTenant } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [linkForm, setLinkForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tenants").then(({ data }) => setTenants(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/tenants", form);
      toast.success("Engagement created — Dinacharya, Panchatanthra and Viveka Kartha are live");
      setOpen(false);
      setForm(BLANK);
      load();
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/tenants/${editing.id}`, { ...form, dinacharya_links: linkForm });
      toast.success("Engagement updated");
      setEditing(null);
      load();
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleStatus = async (t) => {
    try {
      await api.put(`/tenants/${t.id}`, { status: t.status === "active" ? "suspended" : "active" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const bootstrap = async (t) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${t.id}/bootstrap`);
      toast.success(`${t.name}: 3 frameworks active${data.workbooks_copied.length
        ? `, ${data.workbooks_copied.map((w) => w.label).join(" + ")} workbooks copied` : ""}`);
      load(); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const bootstrapAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tenants/bootstrap-all");
      toast.success(`${data.count} engagements now run all three frameworks on the standard workbooks`);
      load(); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-10">
      <PageHeader
        testid="admin-tenants-header"
        eyebrow="Administration"
        title="Client engagements"
        description="Edit engagement details, activate the three frameworks and reuse the standard Panchatanthra and Viveka Kartha workbooks. Tenant isolation stays absolute across APIs, search, exports and notifications."
        actions={
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="bootstrap-all-button" variant="outline" disabled={busy}
              onClick={bootstrapAll}
              className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]"
            >
              Activate D·P·V for all
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-tenant-button" className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">
                  New engagement
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle className="font-display text-2xl">Engagement setup</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  {FIELDS.map(([key, label]) => (
                    <div key={key} className="space-y-2">
                      <Label className="label-caps">{label}</Label>
                      <Input
                        data-testid={`tenant-${key}-input`} className="rounded-none"
                        value={form[key] || ""}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      />
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">
                    All three frameworks are activated and the standard PT + VK workbooks are copied in
                    automatically.
                  </p>
                  <Button data-testid="create-tenant-submit" className="w-full rounded-none"
                          disabled={!form.name} onClick={create}>
                    Create engagement
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-none">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit engagement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {FIELDS.map(([key, label]) => (
              <div key={key} className="space-y-2">
                <Label className="label-caps">{label}</Label>
                <Input
                  data-testid={`edit-tenant-${key}-input`} className="rounded-none"
                  value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              </div>
            ))}

            <div className="space-y-4 border-t border-border pt-5">
              <div>
                <p className="label-caps">Dinacharya links</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Paste this client's Google Sheet or Apps Script web-app URLs. Each one makes the
                  matching card on their Dinacharya screen open in a new tab. Leave blank to hide.
                </p>
              </div>
              {DINACHARYA_LINK_KEYS.map(([key, label]) => (
                <div key={key} className="space-y-2">
                  <Label className="label-caps">{label}</Label>
                  <Input
                    data-testid={`edit-tenant-link-${key}-input`} className="rounded-none"
                    placeholder="https://docs.google.com/… or https://script.google.com/…"
                    value={linkForm[key] || ""}
                    onChange={(e) => setLinkForm({ ...linkForm, [key]: e.target.value })}
                  />
                </div>
              ))}
            </div>

            <Button data-testid="edit-tenant-submit" className="w-full rounded-none"
                    disabled={!form.name} onClick={saveEdit}>Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="panel divide-y divide-border" data-testid="admin-tenants-list">
        {tenants.map((t) => (
          <div key={t.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div>
              <p className="font-display text-lg">{t.name}</p>
              <p className="label-caps mt-1" data-testid={`tenant-meta-${t.id}`}>
                {t.industry || "—"} · {t.country || "—"} ·{" "}
                {(t.frameworks || []).length === 3
                  ? "all three frameworks active"
                  : (t.frameworks || []).join(", ") || "no framework active"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <StateBadge state={t.status} />
              {(t.frameworks || []).length < 3 && (
                <Button data-testid={`bootstrap-tenant-${t.id}`} size="sm" disabled={busy}
                        onClick={() => bootstrap(t)}
                        className="rounded-none text-[10px] uppercase tracking-[0.15em]">
                  Activate D·P·V
                </Button>
              )}
              <Button data-testid={`edit-tenant-${t.id}`} size="sm" variant="outline"
                      onClick={() => {
                        setEditing(t);
                        setForm(FIELDS.reduce((a, [k]) => ({ ...a, [k]: t[k] || "" }), {}));
                        setLinkForm(t.dinacharya_links || {});
                      }}
                      className="rounded-none text-[10px] uppercase tracking-[0.15em]">
                Edit
              </Button>
              <Button data-testid={`switch-tenant-${t.id}`} size="sm" variant="ghost"
                      className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                      onClick={() => { switchTenant(t.id); toast.success(`Context switched to ${t.name}`); }}>
                Switch to
              </Button>
              <Button data-testid={`toggle-tenant-${t.id}`} size="sm" variant="outline"
                      className="rounded-none text-[10px] uppercase tracking-[0.15em]"
                      onClick={() => toggleStatus(t)}>
                {t.status === "active" ? "Suspend" : "Reactivate"}
              </Button>
            </div>
          </div>
        ))}
        {tenants.length === 0 && <EmptyState testid="admin-tenants-empty" title="No engagements" message="Create the first client engagement." />}
      </div>
    </div>
  );
}
